/**
 * 枚举与中文显示名映射 — 与《数据库设计 v2.0》§1 一致：
 * 库里存 UPPER_SNAKE_CASE 明文字符串，中文显示名在前端映射（禁 int 编码）。
 */

export type Tone = 'ok' | 'warn' | 'dan' | 'pri' | 'mut' | 'vio';

export interface EnumMeta<T extends string> {
  label: string;
  tone?: Tone;
  /** 枚举码本身（UPPER_SNAKE_CASE），便于埋点与选项透传 */
  code?: T;
}

export const USER_TYPE = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  OPS_ADMIN: 'OPS_ADMIN',
  DATA_ANALYST: 'DATA_ANALYST',
  DISPATCH_SUPERVISOR: 'DISPATCH_SUPERVISOR',
  BPO_AGENT: 'BPO_AGENT',
  COURIER: 'COURIER',
} as const;
export type UserType = (typeof USER_TYPE)[keyof typeof USER_TYPE];

export const USER_TYPE_META: Record<UserType, EnumMeta<UserType>> = {
  SUPER_ADMIN: { label: '超级管理员' },
  OPS_ADMIN: { label: '运营管理员' },
  DATA_ANALYST: { label: '数据分析师' },
  // t71 重命名（契约 §4 命中 15）：只改显示名，role code 冻结不变。
  DISPATCH_SUPERVISOR: { label: '交付机构管理员' },
  BPO_AGENT: { label: 'BPO客服', tone: 'warn' },
  COURIER: { label: '交付专员', tone: 'pri' },
};

export const USER_STATUS = {
  ACTIVE: 'ACTIVE',
  PENDING_AUDIT: 'PENDING_AUDIT',
  DISABLED: 'DISABLED',
  SUSPENDED: 'SUSPENDED',
} as const;
export type UserStatus = (typeof USER_STATUS)[keyof typeof USER_STATUS];

export const USER_STATUS_META: Record<UserStatus, EnumMeta<UserStatus>> = {
  ACTIVE: { label: '正常', tone: 'ok' },
  PENDING_AUDIT: { label: '待审核', tone: 'warn' },
  DISABLED: { label: '禁用', tone: 'dan' },
  SUSPENDED: { label: '停用', tone: 'mut' },
};

export const USER_END = { ADMIN: 'ADMIN', BPO: 'BPO', MINIAPP: 'MINIAPP' } as const;
export type UserEnd = (typeof USER_END)[keyof typeof USER_END];

export const USER_END_META: Record<UserEnd, EnumMeta<UserEnd>> = {
  ADMIN: { label: '管理端', tone: 'pri' },
  BPO: { label: '录单端', tone: 'warn' },
  MINIAPP: { label: '小程序', tone: 'vio' },
};

/** 端绑定互斥：外部角色仅绑专属端，禁止开通管理端 */
export const EXTERNAL_ENDS: readonly UserEnd[] = [USER_END.BPO, USER_END.MINIAPP];

export const COURIER_LEVEL = { GOLD: 'GOLD', SILVER: 'SILVER', BRONZE: 'BRONZE' } as const;
export type CourierLevel = (typeof COURIER_LEVEL)[keyof typeof COURIER_LEVEL];

export const COURIER_LEVEL_META: Record<CourierLevel, EnumMeta<CourierLevel>> = {
  GOLD: { label: '金牌', tone: 'warn' },
  SILVER: { label: '银牌', tone: 'pri' },
  BRONZE: { label: '铜牌', tone: 'mut' },
};

export const COURIER_DUTY = { ONLINE: 'ONLINE', OFFLINE: 'OFFLINE', ON_LEAVE: 'ON_LEAVE' } as const;
export type CourierDuty = (typeof COURIER_DUTY)[keyof typeof COURIER_DUTY];

export const COURIER_DUTY_META: Record<CourierDuty, EnumMeta<CourierDuty>> = {
  ONLINE: { label: '接单中', tone: 'ok' },
  OFFLINE: { label: '已下线', tone: 'mut' },
  ON_LEAVE: { label: '请假', tone: 'warn' },
};

export const CERT_STATUS = { CERTIFIED: 'CERTIFIED', PENDING: 'PENDING', MISSING: 'MISSING' } as const;
export type CertStatus = (typeof CERT_STATUS)[keyof typeof CERT_STATUS];

export const CERT_STATUS_META: Record<CertStatus, EnumMeta<CertStatus>> = {
  CERTIFIED: { label: '已认证', tone: 'ok' },
  PENDING: { label: '认证中', tone: 'warn' },
  MISSING: { label: '缺失', tone: 'dan' },
};

export const DATA_SCOPE = {
  ALL: 'ALL',
  OWN_REGION: 'OWN_REGION',
  SELF_ONLY: 'SELF_ONLY',
  SELF_ENTERED_ONLY: 'SELF_ENTERED_ONLY',
} as const;
export type DataScope = (typeof DATA_SCOPE)[keyof typeof DATA_SCOPE];

export const DATA_SCOPE_META: Record<DataScope, EnumMeta<DataScope>> = {
  ALL: { label: '全部' },
  OWN_REGION: { label: '本区域（所辖）' },
  SELF_ONLY: { label: '仅本人' },
  SELF_ENTERED_ONLY: { label: '仅本人录入' },
};

/** 订单状态（VARCHAR+CHECK，可扩展）。管理端页签按业务分组聚合多状态。 */
export const ORDER_STATUS = {
  PENDING_ASSIGN: 'PENDING_ASSIGN',
  POOL_PENDING: 'POOL_PENDING',
  ASSIGNED: 'ASSIGNED',
  ACCEPTED: 'ACCEPTED',
  DELIVERING: 'DELIVERING',
  DELIVERED: 'DELIVERED',
  AWAITING_CALLBACK: 'AWAITING_CALLBACK',
  CARRIER_CONFIRMED: 'CARRIER_CONFIRMED',
  COMPLETED: 'COMPLETED',
  EXCEPTION: 'EXCEPTION',
  CANCELLED: 'CANCELLED',
  /* 第 12 码（用户反馈第 6 条；对照表 coord/feedback-20261011/tab-status-mapping.md §一 第 12 行）：
     REJECTED 早在后端在册（code-api src/DeliveryHub.Domain/Enums/Enums.cs:18），且 PENDING_ASSIGN→REJECTED 这条迁移边
     正是本端（Dispatcher）发起（OrderStatusTransitions.cs:49）⇒ 被驳回单本该由本端看。改前本仓把这枚码整枚漏掉，
     页签并集因此不覆盖全集。后端逐码计数口按 `EnumCode.AllCodes<OrderStatus>()` 铺满 12 码键面（ContractGapModule.cs:509），
     故补上该码不新增任何后端接口、不改契约面。 */
  REJECTED: 'REJECTED',
  /* 第 13 码（紧急需求20261029 需求1）：后端 OrderStatus.CARD_FAILED，与交付提交页第五档「开卡失败」同源；
     用户拍板它是**终态**（OrderStatusTransitions.Terminal 已含它）⇒ 不可改派重办、无出边。
     改前本仓没有这枚码，交付端提交后本端列表与详情只能显示原码——三端状态展示同步是本次需求原文。 */
  CARD_FAILED: 'CARD_FAILED',
} as const;
export type OrderStatus = (typeof ORDER_STATUS)[keyof typeof ORDER_STATUS];

export const ORDER_STATUS_META: Record<OrderStatus, EnumMeta<OrderStatus>> = {
  PENDING_ASSIGN: { label: '待分配', tone: 'dan' },
  POOL_PENDING: { label: '公区待领', tone: 'warn' },
  ASSIGNED: { label: '已分配', tone: 'pri' },
  ACCEPTED: { label: '已接单', tone: 'pri' },
  DELIVERING: { label: '交付中', tone: 'pri' },
  DELIVERED: { label: '交付完成', tone: 'ok' },
  /* 反馈文档_09 第 4 条（A 方案：后端状态机与历史数据一律不动，只收敛展示层）：交付完成与待回调在业务上属同一阶段，
     两个状态重复 ⇒ 本端不再把它当一枚独立状态展示：标签并入「交付完成」、状态下拉不再单列（ORDER_STATUS_FILTER_OPTIONS）、
     主流程也不再把它单算一步（ORDER_STATUS_FLOW / nextOrderStatus 走 ORDER_STATUS_DISPLAY_MERGED 归并）。
     （「已驳回」后来也退出下拉，但那是它自己的反馈条号（第 5 条），与本条 A 方案无关，
      见 ORDER_STATUS_FILTER_EXCLUDED_CODES。）
     码值仍在册：列表行里历史单仍可能回这一枚码，StatusTag 依旧能渲染（只是文案与交付完成同）。 */
  AWAITING_CALLBACK: { label: '交付完成', tone: 'ok' },
  CARRIER_CONFIRMED: { label: '运营商确认', tone: 'ok' },
  COMPLETED: { label: '已完结', tone: 'ok' },
  EXCEPTION: { label: '异常', tone: 'dan' },
  CANCELLED: { label: '已取消', tone: 'mut' },
  REJECTED: { label: '已驳回', tone: 'dan' },
  /* 失败族同色调（与 EXCEPTION／REJECTED 一致）：开卡失败是「卡没开成」的业务失败结果，不是进行中态。 */
  CARD_FAILED: { label: '开卡失败', tone: 'dan' },
};

/**
 * 展示层归并表（反馈文档_09 第 4 条 A 方案）：待回调 ⇒ 交付完成。
 * 只描述「怎么显示」，⛔ 不参与任何写面/状态机——码值、后端 OrderStatus、历史单与结算 BillStatus 一律不动。
 */
export const ORDER_STATUS_DISPLAY_MERGED: Partial<Record<OrderStatus, OrderStatus>> = {
  [ORDER_STATUS.AWAITING_CALLBACK]: ORDER_STATUS.DELIVERED,
};

/** 展示层不再单列的码（归并表键集；下拉与阶段都按归并后的口径铺） */
export const ORDER_STATUS_DISPLAY_MERGED_CODES: readonly OrderStatus[] = [
  ORDER_STATUS.AWAITING_CALLBACK,
];

/**
 * 不进状态筛选下拉的码（本轮测试反馈第 5 条：「已驳回」从订单中心状态下拉里移除）。
 * ⛔ 只决定「能不能拿它当筛选项」：码值仍冻结在册、中文标签仍在（labelOf(ORDER_STATUS_META, 'REJECTED')
 * 仍回「已驳回」），「全部」页签里历史单回 REJECTED 时列表行/详情照旧渲染中文 —— 删的只是下拉那一格。
 * ⛔ 也别把它与上方 ORDER_STATUS_TABS 的页签口径混作一回事：页签撤位是 2026-10-13 三.14 那条裁定，
 *   本清单是**下拉**口径；两份清单各自独立，加一枚都要有对应的反馈条号。
 * 与超管后台 code-web-admin/src/core/enums/index.ts 的 ORDER_STATUS_FILTER_EXCLUDED_CODES 同口径同名单。
 */
export const ORDER_STATUS_FILTER_EXCLUDED_CODES: readonly OrderStatus[] = [ORDER_STATUS.REJECTED];

/** 展示用归并：命中归并表走目标码，其余原样返回；未知码也原样返回（⛔ 抛错、⛔ 吞成空串）。 */
export function displayOrderStatusOf(status: OrderStatus | string): OrderStatus | string {
  return ORDER_STATUS_DISPLAY_MERGED[status as OrderStatus] ?? status;
}

/**
 * 状态下拉的**唯一**选项来源。两处收敛，理由各不相同：
 *   ① 已并入交付完成的那枚不再单列（否则下拉里会出现两枚同名「交付完成」，选了也分不清是哪一枚）；
 *   ② 「已驳回」不再进下拉（本轮测试反馈第 5 条：驳回是终态、单量极少，下拉里不再给它留一格）。
 *      ⛔ 只剔选项不删码：「全部」页签与列表行里回 REJECTED 的历史单仍显示「已驳回」（见 labelOf 消费点）。
 * 故本端 12 枚码 ⇒ 下拉 10 枚（−1 归并的待回调、−1 剔除的已驳回），与超管后台同口径。
 * 未知码防线的判据在 labelOf：它回落原码，故这里只负责枚数与文案，不去猜码。
 */
export const ORDER_STATUS_FILTER_OPTIONS: ReadonlyArray<{ value: OrderStatus; label: string }> =
  (Object.keys(ORDER_STATUS_META) as OrderStatus[])
    .filter((code) => !ORDER_STATUS_DISPLAY_MERGED_CODES.includes(code))
    .filter((code) => !ORDER_STATUS_FILTER_EXCLUDED_CODES.includes(code))
    .map((code) => ({ value: code, label: ORDER_STATUS_META[code].label }));

/**
 * 订单主流程顺序（异常 / 已取消为分支态，不在此链上）。
 * V4.9-3 详情「当前状态 交付中 → 下一步」与 mock 的阶段推进共用这一条定义，避免两处各写一份数组。
 * 反馈文档_09 第 4 条 A 方案：待回调与交付完成同阶段 ⇒ 该码**不在这条链上**，历史单回它时由
 * nextOrderStatus 先归并到交付完成再取下一步（详情显示「交付完成 → 运营商确认」，⛔ 再出现「→ 待回调」）。
 */
export const ORDER_STATUS_FLOW: readonly OrderStatus[] = [
  ORDER_STATUS.PENDING_ASSIGN,
  ORDER_STATUS.POOL_PENDING,
  ORDER_STATUS.ASSIGNED,
  ORDER_STATUS.ACCEPTED,
  ORDER_STATUS.DELIVERING,
  ORDER_STATUS.DELIVERED,
  ORDER_STATUS.CARRIER_CONFIRMED,
  ORDER_STATUS.COMPLETED,
];

/** 主流程下一状态；分支态与终态返回 null，详情据此不显示「→ 下一步」。
 *  入参先按展示归并（待回调⇒交付完成），故该码返回的也是「运营商确认」而非 null——它只是交付完成的另一种回写。 */
export function nextOrderStatus(status: OrderStatus): OrderStatus | null {
  const stage = displayOrderStatusOf(status) as OrderStatus;
  const index = ORDER_STATUS_FLOW.indexOf(stage);
  return index >= 0 && index < ORDER_STATUS_FLOW.length - 1 ? ORDER_STATUS_FLOW[index + 1] : null;
}

/**
 * 催派升级策略。命名跟上游原文：`DB-DESIGN-bpo-marketing-delivery.md:123` 的 `URGED:{reason,channel,escalate}`
 * 用的是 **escalate**（⛔ escalation），渠道用 **channel**（⛔ channels）⇒ 旧名 ORDER_URGE_ESCALATION 已废。
 *
 * ⚠ 码值集**未定形**：上游只给了键名，没给取值；冻结契约的写面 `UrgeRequest` 只有 `remark` 与
 *   `expectedRowversion` 两枚 required、未开 `additionalProperties` ⇒ 这三枚催派附加项**没有上送位**。
 *   落地口径：本页仅作本地展示，⛔ 进请求体；要真正生效须等 payload 内层形状定形（item 1c）。
 */
export const URGE_ESCALATE = {
  SECOND_URGE_TO_SUPERVISOR: 'SECOND_URGE_TO_SUPERVISOR',
  TIMEOUT_TO_SUPERVISOR: 'TIMEOUT_TO_SUPERVISOR',
  RECORD_ONLY: 'RECORD_ONLY',
} as const;
export type UrgeEscalate = (typeof URGE_ESCALATE)[keyof typeof URGE_ESCALATE];

export const URGE_ESCALATE_META: Record<UrgeEscalate, EnumMeta<UrgeEscalate>> = {
  SECOND_URGE_TO_SUPERVISOR: { label: '二次催派未响应升级主管', tone: 'dan' },
  TIMEOUT_TO_SUPERVISOR: { label: '超响应时限即升级主管', tone: 'warn' },
  RECORD_ONLY: { label: '仅记录，不升级', tone: 'mut' },
};

/** 催单浮层默认升级策略（本地展示值，⛔ 上送） */
export const URGE_ESCALATE_DEFAULT: UrgeEscalate = URGE_ESCALATE.SECOND_URGE_TO_SUPERVISOR;

/** 催派通知渠道（上游键名 `channel`，单数；同为 item 1c 定形前仅本地展示） */
export const URGE_CHANNEL = { INAPP: 'INAPP', SMS: 'SMS', PHONE: 'PHONE' } as const;
export type UrgeChannel = (typeof URGE_CHANNEL)[keyof typeof URGE_CHANNEL];

export const URGE_CHANNEL_META: Record<UrgeChannel, EnumMeta<UrgeChannel>> = {
  INAPP: { label: '站内信' },
  SMS: { label: '短信' },
  PHONE: { label: '电话' },
};

/**
 * 催派标识（契约 `OrderListItem.urgeFlag`，enum 闭集 **0／1／2 整数**，⛔ 当布尔用）。
 * 展示口径逐字照 PRD §12.4：0→「—」／1→「加急」／2→「催办中」。
 * ⚠ 三态各须有可区分展示位：把 1／2 合并成一态后，「催过已处理完」与「正在催」不可区分，
 *   且催派次数联动会读出错（AC-ADM-03.6）。未知值 ⇒ 原样透传＋异常标记，⛔ 折成 0。
 * ⛔ 前端按 `urgeCount` 自算这枚（＝第二本账）；后端派生规则挂在 item 1c。
 */
export const URGE_FLAG = { NONE: 0, DONE: 1, IN_PROGRESS: 2 } as const;
export type UrgeFlag = (typeof URGE_FLAG)[keyof typeof URGE_FLAG];

export const URGE_FLAG_META: Record<UrgeFlag, { label: string; tone: Tone }> = {
  0: { label: '', tone: 'mut' },
  1: { label: '加急', tone: 'dan' },
  2: { label: '催办中', tone: 'warn' },
};

/**
 * 催派标识的展示文案（整数闭集 ⇒ 走 `labelOf` 之外，那枚泛型约束在 string）。
 * 契约：0→「—」／1→「加急」／2→「催办中」；未知值 ⇒ 原样透传＋异常标记，⛔ 折成 0。
 */
export function urgeFlagLabel(value: number | null | undefined): string {
  if (value === 0) return '—';
  if (value === 1 || value === 2) return URGE_FLAG_META[value].label;
  return value === null || value === undefined ? '—' : `未知(${value})`;
}

export function urgeFlagTone(value: number | null | undefined): Tone {
  return value === 1 || value === 2 ? URGE_FLAG_META[value].tone ?? 'mut' : 'mut';
}

export const ORDER_TYPE = { NEW_CARD: 'NEW_CARD', RENEW: 'RENEW', UPGRADE: 'UPGRADE' } as const;
export type OrderType = (typeof ORDER_TYPE)[keyof typeof ORDER_TYPE];

/** order_type 取值待 PRD 补全（当前以 V4.9 UI + 后端契约为准） */
export const ORDER_TYPE_META: Record<OrderType, EnumMeta<OrderType>> = {
  NEW_CARD: { label: '新办卡' },
  RENEW: { label: '续约' },
  UPGRADE: { label: '升级套餐' },
};

export const DELIVERY_METHOD = {
  ON_SITE: 'ON_SITE',
  EXPRESS: 'EXPRESS',
  SELF_PICKUP: 'SELF_PICKUP',
} as const;
export type DeliveryMethod = (typeof DELIVERY_METHOD)[keyof typeof DELIVERY_METHOD];

/** 冻结取值：后端文档 v1.5 §6.0-⑧（三值保留，明文字符串传输，不出现 int） */
export const DELIVERY_METHOD_META: Record<DeliveryMethod, EnumMeta<DeliveryMethod>> = {
  ON_SITE: { label: '上门交付' },
  EXPRESS: { label: '快递寄送' },
  SELF_PICKUP: { label: '自提' },
};

/**
 * §6.0-⑧：P0 只承诺 ON_SITE（上门）。EXPRESS / SELF_PICKUP 归 P1 —— 六步交付 SOP 与现场
 * 凭证规则均按上门场景设计，另两种缺 SOP 支撑。P0 表单须置灰/隐藏，但字段仍可提交存储。
 */
export const DELIVERY_METHOD_P0: readonly DeliveryMethod[] = [DELIVERY_METHOD.ON_SITE];

export function isDeliveryMethodP0(method: DeliveryMethod): boolean {
  return DELIVERY_METHOD_P0.includes(method);
}

/**
 * 派单方式（契约 `OrderListItem.dispatchMode`，enum 闭集 `PUBLIC_POOL`／`DIRECT_ASSIGN`）。
 * ⚠ 前端旧名 `acceptMethod`（POOL_GRAB／ASSIGN）是我自造的第三套叫法，已废；本枚举逐字照契约。
 * ⚠ 该行面键「⛔ 不占 22 列任何列位」，且详情面 OrderDetail 21 键里没有这枚平键 ⇒ 详情要显示须走
 *   `timeline` 的 GRABBED 事件（后端裁定表 101 §4-1），⛔ 假装有值。
 */
export const DISPATCH_MODE = {
  PUBLIC_POOL: 'PUBLIC_POOL',
  DIRECT_ASSIGN: 'DIRECT_ASSIGN',
} as const;

export type DispatchMode = (typeof DISPATCH_MODE)[keyof typeof DISPATCH_MODE];

/**
 * `dispatchMode` 的标签。⚠ 原先两枚（「公区抢单」／「主管指派」）是前端自造串，doc 103 §1 那句
 * 「逐字照契约」不成立 —— 后端 seq=1577（doc 113 §三）核过：`Enums.cs:29` 是裸枚举、出参只有成员名原文，
 * 中文规范名在 **PRD §11.3 术语行（`:1061`）／§12.4 值域行（`:1243`）**，逐字在契约 `dispatchMode.description` 里。
 * 项负责 seq=1590 ② 裁**乙**之后，浮层「接单方式」那格改读事件码、⛔ 读本表（来源事实 ≠ 动作事实）；
 * 本表留给**列表页分档**那一待裁（行面新增来源事件位＝契约版本跃迁，并入 seq=1515 那批）。
 */
export const DISPATCH_MODE_META: Record<DispatchMode, EnumMeta<DispatchMode>> = {
  PUBLIC_POOL: { label: '公区（抢单池）', tone: 'vio' },
  DIRECT_ASSIGN: { label: '直派', tone: 'pri' },
};

/**
 * 订单中心状态页签（本批起 11 枚：全部 + 10 枚状态页签）。
 * ⚠ 需求 2（V0.0.2）：后端停止写 `ASSIGNED`、存量改派进 `ACCEPTED`，本端随之收起 `assigned`「已分配／待领取」页签；
 *   码仍在册（历史行只读显示、状态下拉仍可筛存量），与 `REJECTED` 一样不进「页签并集覆盖」分母。
 * ⚠ 2026-10-13 用户裁定（《测试反馈文档_04》三.14「订单中心仍保留『已驳回』页签，属冗余需清理」）：
 *   删掉 `rejected` 整枚页签位。`REJECTED` **仍是** `OrderStatus` 12 码全集里的第 12 码（见本文件 :118/:122/:138 ——
 *   后端在册、PENDING_ASSIGN→REJECTED 迁移边在册、状态标签仍要能把被驳回单显示成「已驳回」），
 *   撤的只是「订单中心给它开一枚页签位」这件事，⛔ 不是把码从枚举/标签/口径里删掉。
 *   代价明写（⛔ 不藏）：页签并集从此**不覆盖全码集**，被驳回单只在「全部」里可见；页签计数之和也不再恒等于
 *   契约 `total`，差额＝本机构可见范围内 `REJECTED` 的条数（本批那份 2026-10-11 活体读数该码为 0，故差额为 0）。
 *   ⛔ 不为了让等式好看而把 `REJECTED` 偷挂进别的页签 —— 那是重码重复计，会回退到 2026-10-11 那次的 52≠50。
 * ⚠ 用户 m01209 第 2 条「移除『预约配送』页签」的裁定已由 2026-10-12 新流程裁定**作废**：预约配送＝`DELIVERING`，
 *   该页签按新流程恢复（覆盖码仍是 DELIVERING，见 coord/feedback-20261011/tab-status-mapping.md 与 
 *   coord/feedback2-20261012/backend-delivery-flow.md §5）。
 * 页签为固定定义，计数来自 §6.2-1 `GET /orders/status-counts` 的**逐码**回体（后端 `SqlStatusCountsQuery` GROUP BY status、
 * `GapEndpoints.OrderStatusCodes` 铺满 `EnumCode.AllCodes<OrderStatus>()` ⇒ **12 码**键面，ContractGapModule.cs:509），
 * 因此码值缺失按 0 计，页签数量不随数据抖动。
 *
 * ⚠ D-F4-19 订正（t40）：原先 `wait`「待分配」桶把 `POOL_PENDING` 跨码求和进来（旧 :293 = [PENDING_ASSIGN, POOL_PENDING]），
 * 使公区待领单在页签口径里被算作「待分配」且无独立归口（复测实测 待分配 14 = PENDING_ASSIGN 12 + POOL_PENDING 2；
 * 差异分类 ③PRD 歧义、级别低）。现改为一桶一码：`wait` 只含 `PENDING_ASSIGN`，`POOL_PENDING` 单列 `pool`「公区待领」桶。
 *
 * ⚠ 用户反馈第 6 条对齐（本次；对照表＝coord/feedback-20261011/tab-status-mapping.md 为两仓唯一真源）：
 * ① 同一枚码不许落进两枚同义页签：改前 `failed`「交付失败」与 `abnormal`「异常」都只含 `EXCEPTION`，同一批单被两枚页签
 *    各算一遍 ⇒ 页签计数之和 52 ≠ 总数 50（2026-10-11 19:10:06 +08:00 逐码实测复现，见台账）。`EXCEPTION` 单归口到「异常」，
 *    「交付失败」整枚删除（全仓零消费点：唯一读取处是按 key 动态取的两处）。
 * ② 「交付完成」只留 `DELIVERED`：改前 `done` 并入 `COMPLETED`，而超管端已拆开 ⇒ 同义页签码集不同；`COMPLETED` 单列
 *    `end`「已完结」，与超管端同码集。
 * ③ 补 `rejected`「已驳回」（t40）—— ⚠ **已于本批按 2026-10-13 三.14 整体撤回**：撤的是页签位（本表 :335 那段改写），
 *    ⛔ 不是把 `REJECTED` 从码全集/枚举/中文标签里删掉（该码仍在册，见上面 :118/:122/:138 与后端 Enums.cs:18）。
 *    ⚠ 2026-10-21 本轮反馈第 5 条：**状态筛选下拉**里也撤掉「已驳回」那一格（该口径在 ORDER_STATUS_FILTER_EXCLUDED_CODES，
 *    与页签无关）。故本端的「已驳回」可见面只剩两处：`all`「全部」页签里的行、以及行/详情上的中文标签。
 * 对账口径：10 枚状态页签计数之和 = 逐码计数之和 **减去不外露的 `REJECTED`／`ASSIGNED` 条数**（两份裁定的代价，见开头）；
 *   `all` 页签＝契约 `total`（含被驳回单，⛔ 下拉剔除不影响本等式 —— 下拉只管单码筛选，不计总数）。
 * ⛔ 本表只含状态页签，不含任何非状态筛选维度（用户 m01209 裁定后已清空该字段）。
 * ⛔ 本表仍是**前端分组**而非服务端口径；若产品确认「待分配」应含公区单，须由产品裁定后回改（见 t40 交付物）。
 */
export interface OrderStatusTab {
  key: string;
  label: string;
  statuses: OrderStatus[];
  /* 用户 m01209 裁定：本表不再有非状态筛选维度（原非状态筛选字段已随「预约配送」页签一并移除）。 */
}

export const ORDER_STATUS_TABS: readonly OrderStatusTab[] = [
  { key: 'all', label: '全部', statuses: [] },
  { key: 'wait', label: '待分配', statuses: [ORDER_STATUS.PENDING_ASSIGN] },
  { key: 'pool', label: '公区待领', statuses: [ORDER_STATUS.POOL_PENDING] },

  /* 需求 2（V0.0.2）：不再有「已分配／待领取」页签——后端停止写 ASSIGNED、存量改派进待处理（ACCEPTED），
     读侧这一档随之收起。ASSIGNED 码仍在册（:108 常量、:133 标签）供历史行只读显示，
     ⛔ 别为「补漏码」把这枚页签加回来；状态下拉仍保留该码，仅用于查存量历史单。 */
  { key: 'accepted', label: '已接单', statuses: [ORDER_STATUS.ACCEPTED] },
  /* 预约配送（2026-10-12 新流程）：覆盖码＝DELIVERING——全仓唯一「货未交付、可上传凭证」的在途态，
     与文档「进入预约配送才可上传照片并确认交付」逐字同构。本轮沿用原「交付中」那一枚**改名**而非再加一枚，
     ⛔ 同一枚码不进两枚页签（重码会让页签计数之和 ≠ 总数）。 */
  { key: 'appoint', label: '预约配送', statuses: [ORDER_STATUS.DELIVERING] },
  /* 交付完成只留 DELIVERED（对照表第一节第 6 行）：改前 done 并入 COMPLETED，与超管端「交付完成」码集不同。 */
  { key: 'done', label: '交付完成', statuses: [ORDER_STATUS.DELIVERED] },
  { key: 'end', label: '已完结', statuses: [ORDER_STATUS.COMPLETED] },
  { key: 'carrier', label: '运营商确认', statuses: [ORDER_STATUS.AWAITING_CALLBACK, ORDER_STATUS.CARRIER_CONFIRMED] },
  /* EXCEPTION 单归口（对照表第一节第 10 行／第三节）：改前 failed「交付失败」与 abnormal「异常」两枚都只含它，
     同一批单被两枚页签各算一遍，页签计数之和 52 ≠ 总数 50。删掉的是「交付失败」整枚，不是把码改挂别处。 */
  { key: 'abnormal', label: '异常', statuses: [ORDER_STATUS.EXCEPTION] },
  /* 开卡失败单列一格（紧急需求20261029 需求1）：⛔ 并进 abnormal「异常」或 cancel「取消」——
     那是「交付过程出错／用户撤销」，与「客户资料到了、卡开不出来」是两种业务结果；且同一枚码进两枚页签会重码计数。
     位置放在 abnormal 之后、cancel 之前：失败终态聚在一起，取消单独收尾。 */
  { key: 'cardfailed', label: '开卡失败', statuses: [ORDER_STATUS.CARD_FAILED] },
  { key: 'cancel', label: '取消', statuses: [ORDER_STATUS.CANCELLED] },
  /* ⛔ 这里**不再**有 `rejected`「已驳回」页签位（2026-10-13 三.14 裁定，理由与代价见上方本表头注第三段）。
     `REJECTED` 码本身仍在册（:122 常量、:138 标签），被驳回单只在「全部」页签里可见；
     ⛔ 别为了「补上漏码」把这枚页签加回来，也别把该码挂进 `abnormal`「异常」或 `cancel`「取消」——那是重码重复计。 */
];

/* 运营商字典**只此一套**：`PRODUCT_CARRIER`／`PRODUCT_CARRIER_META`／`carrierLabelOf` 都在
   core/utils/carrierJobNos.ts（四值，含中国广电，与后端 ProductCarrier 同源）。
   ⛔ 本文件原来那枚三值字典（CARRIER／CARRIER_META，只有电信／联通／移动、码值形态也与后端不同）
   已按用户 2026-10-14「运营商的枚举应该共用一套，不要这一套那一套」整枚删除；读面历史旧码的标签
   兼容只在 LEGACY_CARRIER_ALIAS 一处，⛔ 别再往本文件加第三张运营商表。 */

export const ORDER_EVENT_TYPE = {
  CREATED: 'CREATED',
  /* 紧急需求20261029 需求1：交付端提交「开卡失败」时后端落的订单事件
     （CoreModules.cs 提交口 g.Transition(id, OrderStatus.CARD_FAILED, "CARD_FAILED", OrderEventType.CARD_FAILED, …)）。
     时间线要认得它，否则本端详情只会显示原始码。 */
  CARD_FAILED: 'CARD_FAILED',
  VALIDATED: 'VALIDATED',
  ASSIGNED: 'ASSIGNED',
  TO_POOL: 'TO_POOL',
  GRABBED: 'GRABBED',
  RECALLED: 'RECALLED',
  ACCEPTED: 'ACCEPTED',
  CALL_CONTACT: 'CALL_CONTACT',
  DELIVER_START: 'DELIVER_START',
  DELIVERED: 'DELIVERED',
  PROOF_UPLOADED: 'PROOF_UPLOADED',
  CARRIER_CALLBACK: 'CARRIER_CALLBACK',
  ACTIVATED: 'ACTIVATED',
  FIRST_RECHARGE: 'FIRST_RECHARGE',
  RETENTION_CHECK: 'RETENTION_CHECK',
  URGED: 'URGED',
  REASSIGNED: 'REASSIGNED',
  EXCEPTION: 'EXCEPTION',
  SECOND_FOLLOW: 'SECOND_FOLLOW',
  CANCELLED: 'CANCELLED',
} as const;
export type OrderEventType = (typeof ORDER_EVENT_TYPE)[keyof typeof ORDER_EVENT_TYPE];

export const ORDER_EVENT_META: Record<OrderEventType, EnumMeta<OrderEventType>> = {
  CREATED: { label: '订单录入' },
  VALIDATED: { label: '四项校验' },
  ASSIGNED: { label: '分配' },
  TO_POOL: { label: '放入公区' },
  GRABBED: { label: '抢单' },
  RECALLED: { label: '回收' },
  ACCEPTED: { label: '接单' },
  CALL_CONTACT: { label: '电话联系' },
  DELIVER_START: { label: '开始交付' },
  DELIVERED: { label: '交付完成' },
  PROOF_UPLOADED: { label: '凭证上传' },
  CARRIER_CALLBACK: { label: '运营商回调' },
  ACTIVATED: { label: '激活' },
  FIRST_RECHARGE: { label: '首充' },
  RETENTION_CHECK: { label: '留存巡检' },
  URGED: { label: '催办' },
  REASSIGNED: { label: '改派' },
  EXCEPTION: { label: '异常' },
  SECOND_FOLLOW: { label: '二次跟进' },
  CANCELLED: { label: '取消' },
  CARD_FAILED: { label: '开卡失败' },
};

export const PROOF_TYPE = {
  CARD_PHOTO: 'CARD_PHOTO',
  ACTIVATION_PAGE: 'ACTIVATION_PAGE',
  SCENE_PHOTO: 'SCENE_PHOTO',
  SIGNATURE: 'SIGNATURE',
} as const;
export type ProofType = (typeof PROOF_TYPE)[keyof typeof PROOF_TYPE];

export const PROOF_TYPE_META: Record<ProofType, EnumMeta<ProofType>> = {
  CARD_PHOTO: { label: '卡品照' },
  ACTIVATION_PAGE: { label: '激活页' },
  SCENE_PHOTO: { label: '现场照' },
  SIGNATURE: { label: '客户签名' },
};

/** 交付完成规则：≥3 类凭证 + 签名 */
export const PROOF_MIN_KINDS = 3;

export const REJECT_REASON = {
  INCOMPLETE_INFO: 'INCOMPLETE_INFO',
  DUPLICATE_ENTRY: 'DUPLICATE_ENTRY',
  BLACKLIST: 'BLACKLIST',
  INVALID_NUMBER: 'INVALID_NUMBER',
  OTHER: 'OTHER',
} as const;
export type RejectReason = (typeof REJECT_REASON)[keyof typeof REJECT_REASON];

export const REJECT_REASON_META: Record<RejectReason, EnumMeta<RejectReason>> = {
  INCOMPLETE_INFO: { label: '信息不全', tone: 'warn' },
  DUPLICATE_ENTRY: { label: '重复录入', tone: 'dan' },
  BLACKLIST: { label: '命中黑名单', tone: 'dan' },
  INVALID_NUMBER: { label: '号码无效', tone: 'dan' },
  OTHER: { label: '其他', tone: 'mut' },
};

export const APPEAL_STATUS = {
  NONE: 'NONE',
  PENDING_REEDIT: 'PENDING_REEDIT',
  APPEALING: 'APPEALING',
  UPHELD: 'UPHELD',
  REJECTED: 'REJECTED',
} as const;
export type AppealStatus = (typeof APPEAL_STATUS)[keyof typeof APPEAL_STATUS];

export const APPEAL_STATUS_META: Record<AppealStatus, EnumMeta<AppealStatus>> = {
  NONE: { label: '无', tone: 'mut' },
  PENDING_REEDIT: { label: '待重编辑', tone: 'warn' },
  APPEALING: { label: '申诉中', tone: 'pri' },
  UPHELD: { label: '申诉成立', tone: 'ok' },
  REJECTED: { label: '申诉驳回', tone: 'dan' },
};

export const ALERT_LEVEL = { HIGH: 'HIGH', MEDIUM: 'MEDIUM', LOW: 'LOW' } as const;
export type AlertLevel = (typeof ALERT_LEVEL)[keyof typeof ALERT_LEVEL];

export const ALERT_LEVEL_META: Record<AlertLevel, EnumMeta<AlertLevel>> = {
  HIGH: { label: '高', tone: 'dan' },
  MEDIUM: { label: '中', tone: 'warn' },
  LOW: { label: '低', tone: 'pri' },
};

export const ALERT_TICKET_STATUS = {
  PENDING_CLAIM: 'PENDING_CLAIM',
  PROCESSING: 'PROCESSING',
  CLOSED: 'CLOSED',
  ESCALATED: 'ESCALATED',
} as const;
export type AlertTicketStatus = (typeof ALERT_TICKET_STATUS)[keyof typeof ALERT_TICKET_STATUS];

export const ALERT_TICKET_STATUS_META: Record<AlertTicketStatus, EnumMeta<AlertTicketStatus>> = {
  PENDING_CLAIM: { label: '待认领', tone: 'warn' },
  PROCESSING: { label: '处理中', tone: 'pri' },
  CLOSED: { label: '已关闭', tone: 'ok' },
  ESCALATED: { label: '已升级', tone: 'dan' },
};

export const AUDIT_ACTION = {
  LOGIN: 'LOGIN',
  LOGOUT: 'LOGOUT',
  ORDER_CREATE: 'ORDER_CREATE',
  ORDER_ASSIGN: 'ORDER_ASSIGN',
  ORDER_URGE: 'ORDER_URGE',
  ORDER_CANCEL: 'ORDER_CANCEL',
  EXPORT_REQUEST: 'EXPORT_REQUEST',
  EXPORT_APPROVED: 'EXPORT_APPROVED',
  ROLE_PERM_CHANGE: 'ROLE_PERM_CHANGE',
  RULE_CHANGE: 'RULE_CHANGE',
  CONFIG_CHANGE: 'CONFIG_CHANGE',
  COURIER_AUDIT: 'COURIER_AUDIT',
} as const;
export type AuditAction = (typeof AUDIT_ACTION)[keyof typeof AUDIT_ACTION];

export const AUDIT_ACTION_META: Record<AuditAction, EnumMeta<AuditAction>> = {
  LOGIN: { label: '登录' },
  LOGOUT: { label: '登出' },
  ORDER_CREATE: { label: '订单录入' },
  ORDER_ASSIGN: { label: '订单分配' },
  ORDER_URGE: { label: '订单催办' },
  ORDER_CANCEL: { label: '订单取消' },
  EXPORT_REQUEST: { label: '导出申请' },
  EXPORT_APPROVED: { label: '导出审批' },
  ROLE_PERM_CHANGE: { label: '权限变更' },
  RULE_CHANGE: { label: '规则变更' },
  CONFIG_CHANGE: { label: '配置变更' },
  COURIER_AUDIT: { label: '专员审核' },
};

/** @deprecated 仅供显式 mock 的历史离线夹具；不构成业务授权范围或真实区域目录。 */
export const REGION_CODES = ['WUHOU', 'GAOXIN', 'JINJIANG', 'CHENGHUA', 'SHUANGLIU'] as const;
/** 区域码来自全国行政区划API，不再限制为历史成都五个夹具码。 */
export type RegionCode = string;

/** @deprecated 仅供显式 mock 历史夹具。真实页面必须使用接口原名或原 code，禁止用此表补名。
 * 保留导出是为不破坏既有离线 mock 消费者；不新增区域，也不恢复业务订单样例。
 */
export const REGION_META: Record<RegionCode, { label: string; city: string }> = {
  WUHOU: { label: '武侯区', city: '成都市' },
  GAOXIN: { label: '高新区', city: '成都市' },
  JINJIANG: { label: '锦江区', city: '成都市' },
  CHENGHUA: { label: '成华区', city: '成都市' },
  SHUANGLIU: { label: '双流区', city: '成都市' },
};

/**
 * 解黑结果（变更③ seq=77 产品确认口径，seq=353 全量清单）：
 * 号码黑名单移除操作的结果状态。数据来源＝运营商接口回调或超管后台人工审核记录。
 * 4 态闭集，⛔ 前端把 null／undefined 折算成 PENDING（那是把「无结论」伪装成「待处理」）。
 */
export const UNBLOCK_RESULT = {
  PENDING: 'PENDING',
  UNBLOCKED: 'UNBLOCKED',
  FAILED: 'FAILED',
  NOT_REQUIRED: 'NOT_REQUIRED',
} as const;
export type UnblockResult = (typeof UNBLOCK_RESULT)[keyof typeof UNBLOCK_RESULT];

export const UNBLOCK_RESULT_META: Record<UnblockResult, EnumMeta<UnblockResult>> = {
  PENDING: { label: '待处理', tone: 'warn' },
  UNBLOCKED: { label: '已解黑', tone: 'ok' },
  FAILED: { label: '解黑失败', tone: 'dan' },
  NOT_REQUIRED: { label: '无需解黑', tone: 'mut' },
};

/** 变更③ 筛选面用的 API 码（seq=77 后端草案：小写形式，与库内 UPPER_SNAKE_CASE 分离） */
export const UNBLOCK_RESULT_API_CODE: Record<UnblockResult, 'pending' | 'unblocked' | 'failed' | 'not_required'> = {
  PENDING: 'pending',
  UNBLOCKED: 'unblocked',
  FAILED: 'failed',
  NOT_REQUIRED: 'not_required',
};

export function labelOf<T extends string>(
  meta: Record<T, EnumMeta<T>>,
  value: T | string | null | undefined,
  fallback = '—',
): string {
  if (!value) return fallback;
  const hit = meta[value as T];
  return hit ? hit.label : String(value);
}

/**
 * 外部响应枚举值归一：§6.0-③ 约定线上一律 `UPPER_SNAKE_CASE` 明文，但文档描述性写法
 * （如 §6.2-7 `endBinding` 写作 `admin` / `bpo` / `miniapp`）与真实序列化可能大小写不一致。
 * 按大写归一后在**封闭集**内匹配，未命中回落 `fallback` —— 不猜测、不静默转成相近值。
 */
export function normalizeEnum<T extends string>(raw: unknown, allowed: readonly T[], fallback: T): T {
  return normalizeEnumOrNull(raw, allowed) ?? fallback;
}

/** 同上，但未命中返回 `null`（用于可空字段，避免把未知值伪造成合法值） */
export function normalizeEnumOrNull<T extends string>(raw: unknown, allowed: readonly T[]): T | null {
  if (raw === null || raw === undefined) return null;
  const exact = allowed.find((code) => code === raw);
  if (exact) return exact;
  const upper = String(raw).trim().toUpperCase();
  return allowed.find((code) => code === upper) ?? null;
}

export function toneOf<T extends string>(
  meta: Record<T, EnumMeta<T>>,
  value: T | string | null | undefined,
): Tone | undefined {
  if (!value) return undefined;
  return meta[value as T]?.tone;
}

/** 枚举 → 下拉选项 */
export function optionsOf<T extends string>(
  meta: Record<T, EnumMeta<T>>,
): Array<{ value: T; label: string }> {
  return (Object.keys(meta) as T[]).map((key) => ({ value: key, label: meta[key].label }));
}
